import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { COLORS } from "../../../constants";
import {
  detalheListaFornecedor,
  formatarDataReferenciaBR,
  linhasPdfFichaTransferencia,
  linhasPdfListaFornecedor,
  listasPreenchidas,
  nomeArquivoFichaTransferencia,
  type PdfFichaTransferenciaInput,
} from "./fichaTransferenciaPdfDados";

export type { PdfFichaTransferenciaInput } from "./fichaTransferenciaPdfDados";
export {
  formatarDataReferenciaBR,
  linhasPdfFichaTransferencia,
  nomeArquivoFichaTransferencia,
  textoConsolidadoFichaTransferencia,
} from "./fichaTransferenciaPdfDados";

const LOGO_PATH = "/dom_belloni_logo.png";
const LOGO_LARGURA = 28;
const LOGO_ALTURA = (LOGO_LARGURA * 169) / 260;

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function blobParaDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export async function carregarLogoFichaTransferencia(): Promise<string | null> {
  if (typeof fetch !== "function") return null;
  try {
    const resposta = await fetch(LOGO_PATH);
    if (!resposta.ok) return null;
    return blobParaDataUrl(await resposta.blob());
  } catch {
    return null;
  }
}

function desenharLogo(
  doc: jsPDF,
  logo: string | null,
  x: number,
  y: number,
  largura = LOGO_LARGURA
) {
  if (!logo) return 0;
  const altura = (largura * 169) / 260;
  doc.addImage(logo, "PNG", x, y, largura, altura);
  return largura + 4;
}

export function criarPdfFichaTransferencia(
  input: PdfFichaTransferenciaInput,
  logo: string | null = null
): jsPDF {
  const primary = hexToRgb(COLORS.PRIMARY);
  const margin = 14;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const dataFicha = formatarDataReferenciaBR(input.dataReferencia);
  const conteudoW = pageW - margin * 2;

  const desenharCabecalho = () => {
    const topo = 10;
    const deslocamento = desenharLogo(doc, logo, margin, topo);
    const textoX = margin + deslocamento;
    doc.setTextColor(24, 24, 24);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text("FICHA DE TRANSFERÊNCIA", textoX, topo + 6.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(82, 82, 82);
    doc.text(`${input.origem} para ${input.destino}`, textoX, topo + 12);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(primary[0], primary[1], primary[2]);
    doc.text("FICHA DIÁRIA", pageW - margin, topo + 3.5, { align: "right" });
    doc.setFontSize(16);
    doc.setTextColor(24, 24, 24);
    doc.text(dataFicha, pageW - margin, topo + 10.5, { align: "right" });

    const linhaY = topo + Math.max(LOGO_ALTURA, 16) + 2;
    doc.setDrawColor(primary[0], primary[1], primary[2]);
    doc.setLineWidth(0.7);
    doc.line(margin, linhaY, pageW - margin, linhaY);

    const caixaY = linhaY + 3.5;
    const caixaH = 26;
    doc.setFillColor(250, 248, 247);
    doc.setDrawColor(232, 226, 224);
    doc.setLineWidth(0.2);
    doc.roundedRect(margin, caixaY, conteudoW, caixaH, 1.5, 1.5, "FD");
    doc.setFillColor(primary[0], primary[1], primary[2]);
    doc.rect(margin, caixaY, 1.4, caixaH, "F");

    const colunas = [
      ["Origem", input.origem],
      ["Destino", input.destino],
      ["Ficha diária", dataFicha],
      ["Previsão de entrega", input.previsaoEntrega],
    ];
    colunas.forEach((par, indice) => {
      const coluna = indice % 2;
      const linha = Math.floor(indice / 2);
      const x = margin + 5 + coluna * (conteudoW / 2);
      const destaque = indice >= 2;
      const y = caixaY + 6 + linha * 12;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(120, 113, 108);
      doc.text(par[0].toUpperCase(), x, y);
      doc.setFont("helvetica", destaque ? "bold" : "normal");
      doc.setFontSize(destaque ? 13 : 10);
      doc.setTextColor(24, 24, 24);
      doc.text(par[1], x, y + (destaque ? 5.2 : 4.4));
    });

    return caixaY + caixaH + 6;
  };

  const paginasComCabecalho = new Set<number>([1]);
  const desenharCabecalhoContinuacao = () => {
    const pagina = doc.getCurrentPageInfo().pageNumber;
    if (paginasComCabecalho.has(pagina)) return;
    paginasComCabecalho.add(pagina);
    const deslocamento = desenharLogo(doc, logo, margin, 6, 16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(24, 24, 24);
    doc.text("FICHA DE TRANSFERÊNCIA", margin + deslocamento, 11);
    doc.setFontSize(7);
    doc.setTextColor(primary[0], primary[1], primary[2]);
    doc.text("FICHA DIÁRIA", pageW - margin, 8, { align: "right" });
    doc.setFontSize(12);
    doc.setTextColor(24, 24, 24);
    doc.text(dataFicha, pageW - margin, 13.5, { align: "right" });
    doc.setDrawColor(primary[0], primary[1], primary[2]);
    doc.setLineWidth(0.5);
    doc.line(margin, 18, pageW - margin, 18);
  };

  const desenharTituloSecao = (y: number, titulo: string, detalhe = "") => {
    const alturaBarra = detalhe ? 11.5 : 6.5;
    doc.setFillColor(primary[0], primary[1], primary[2]);
    doc.rect(margin, y, 1.4, alturaBarra, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(24, 24, 24);
    doc.text(titulo, margin + 4, y + 4.8);
    if (detalhe) {
      doc.setFontSize(9.5);
      doc.setTextColor(82, 82, 82);
      doc.text(detalhe, margin + 4, y + 9.8);
    }
    return y + alturaBarra + 2.5;
  };

  const estiloTabela = {
    showHead: "everyPage" as const,
    rowPageBreak: "avoid" as const,
    theme: "grid" as const,
    styles: {
      font: "helvetica",
      fontSize: 10,
      textColor: [24, 24, 24] as [number, number, number],
      lineColor: [38, 38, 38] as [number, number, number],
      lineWidth: 0.15,
      cellPadding: { top: 2.4, bottom: 2.4, left: 3, right: 3 },
      overflow: "linebreak" as const,
      valign: "middle" as const,
    },
    headStyles: {
      fillColor: [243, 243, 243] as [number, number, number],
      textColor: [24, 24, 24] as [number, number, number],
      fontStyle: "bold" as const,
      halign: "center" as const,
      fontSize: 9,
    },
    alternateRowStyles: {
      fillColor: [252, 252, 252] as [number, number, number],
    },
    columnStyles: {
      0: { cellWidth: conteudoW - 46, halign: "left" as const },
      1: { cellWidth: 46, halign: "center" as const, fontStyle: "bold" as const },
    },
    margin: { left: margin, right: margin, top: 22, bottom: 16 },
  };

  const desenharTabela = (startY: number, body: string[][]) => {
    autoTable(doc, {
      startY,
      head: [["Produto", "Quantidade"]],
      body,
      ...estiloTabela,
      didDrawPage: desenharCabecalhoContinuacao,
    });
    return (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable
      ?.finalY ?? startY;
  };

  let cursorY = desenharTituloSecao(desenharCabecalho(), "FICHA DIÁRIA DE PRODUÇÃO");
  cursorY = desenharTabela(cursorY, linhasPdfFichaTransferencia(input.itens));

  for (const lista of listasPreenchidas(input.listas)) {
    cursorY += 8;
    if (cursorY + 30 > pageH - 16) {
      doc.addPage();
      desenharCabecalhoContinuacao();
      cursorY = 24;
    }
    cursorY = desenharTituloSecao(
      cursorY,
      (lista.fornecedor.trim() || "Fornecedor").toUpperCase(),
      detalheListaFornecedor(lista, input.previsaoEntrega)
    );
    const linhas = linhasPdfListaFornecedor(lista.itens);
    cursorY = desenharTabela(cursorY, linhas.length ? linhas : [["Nenhum item", "—"]]);
  }

  const total = doc.getNumberOfPages();
  for (let pagina = 1; pagina <= total; pagina += 1) {
    doc.setPage(pagina);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 113, 108);
    doc.text("Dom Belloni · Ficha de transferência", margin, pageH - 8);
    doc.text(`${pagina} / ${total}`, pageW - margin, pageH - 8, {
      align: "right",
    });
  }

  return doc;
}

export async function baixarPdfFichaTransferencia(
  input: PdfFichaTransferenciaInput
): Promise<void> {
  const logo = await carregarLogoFichaTransferencia();
  const doc = criarPdfFichaTransferencia(input, logo);
  doc.save(nomeArquivoFichaTransferencia(input.dataReferencia));
}
